import {
  CuratedProductListParametersType,
  DiscoveryProductsRecentlyAddedResponseType,
} from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/shallow";
import { SpinnerLegacy } from "../../../components";
import { BestPricePromiseDialog } from "../../../components/bestPricePromise/BestPricePromiseDialog";
import { useProductCuratedList } from "../../../hooks/api/v2/product/useProductCuratedList";
import { useEvents } from "../../../hooks/events/useEvents";
import { useDisplaySizeVariant } from "../../../hooks/useDisplaySizeVariant";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { getSellarWebsiteUtms } from "../../../utils/utmHelper";
import { DiscoveryProductDialog } from "../../discovery/v2/components/DiscoveryProductDialog";
import {
  useDiscoveryV2RecentlyViewedStore,
  setRecentlyViewedExpiry,
} from "../../discovery/v2/DiscoveryV2RecentlyViewedStore";
import { abvFilterType } from "../../discovery/v2/DiscoveryV2SearchStore";
import { useSpotlightFilterStore } from "../SpotlightFilterStore";
import { AwardSpotlightFilterContainerCard } from "./AwardSpotlightFilterContainerCard";
import { SpotlightFilterContainerCard } from "./SpotlightFilterContainerCard";
import { SpotlightProductCard } from "./SpotlightProductCard";

export const SpotlightProductList = ({
  ids,
  query,
  search,
  showFilters = true,
  className,
  showAwardBadge,
}: {
  ids?: number[];
  query?: CuratedProductListParametersType["query"];
  search?: string;
  showFilters?: boolean;
  className?: string;
  showAwardBadge?: boolean;
}) => {
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const [selectedProduct, setSelectedProduct] = useState<
    DiscoveryProductsRecentlyAddedResponseType[number] | undefined
  >();
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get("productId");

  if (!ids && !query && !search)
    throw new Error("Either ids or query must be provided");

  const {
    selectedContainerTypes,
    selectedAbvTypes,
    selectedDietaryTypes,
    offersSellarCreditOnly,
    filterStoreSearch,
  } = useSpotlightFilterStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
      offersSellarCreditOnly: state.offersSellarCreditOnly,
      filterStoreSearch: state.search,
    })),
  );

  const productListQuery = useProductCuratedList(
    {
      query: [
        ...(query?.length ? [...query] : []),
        ...(ids?.length
          ? ([
              {
                type: "id",
                modifier: "IN",
                value: ids,
              },
            ] as const)
          : []),
        ...(offersSellarCreditOnly
          ? ([
              {
                type: "offersSellarCreditOnly",
                modifier: "EQUALS",
                value: offersSellarCreditOnly,
              },
            ] as const)
          : []),
        ...(selectedAbvTypes.length
          ? selectedAbvTypes.map((type) => abvFilterType[type].query)
          : []),
        ...(selectedDietaryTypes.length
          ? ([
              {
                type: "dietaryType",
                modifier: "IN",
                value: selectedDietaryTypes,
              },
            ] as const)
          : []),
        ...(selectedContainerTypes.length
          ? ([
              {
                type: "containerType",
                modifier: "IN",
                value: selectedContainerTypes,
              },
            ] as const)
          : []),
      ],
      search: search ?? filterStoreSearch ?? undefined,
      orderBy: "random",
      limit: 50,
    },
    {
      staleTime: 1000 * 60 * 10,
    },
  );

  const productList =
    productListQuery.data?.pages.flatMap((page) => page) ?? [];

  const selectedProductFromParam = productId
    ? productList.find((product) => parseInt(productId) === product.id)
    : undefined;
  const [previousProductFromParam, setPreviousProductFromParam] = useState(
    selectedProductFromParam,
  );
  if (selectedProductFromParam !== previousProductFromParam) {
    setPreviousProductFromParam(selectedProductFromParam);
    if (selectedProductFromParam) {
      setSelectedProduct(selectedProductFromParam);
    }
  }

  const isLoading = productListQuery.isLoading;

  const onProductClicked = (
    product: DiscoveryProductsRecentlyAddedResponseType[number],
  ) => {
    if (product.Supplier) {
      setSelectedProduct(product);
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: product.id,
        supplierId: product.Supplier.id,
        name: product.name ?? "",
        imageUrl: product.imageUrl,
        logoUrl: product.Supplier.Business?.logoUrl,
        urlId: product.Supplier.urlId,
        details: `${product.kind} // ${product.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: product.containsPreOrderProductItem,
      });

      track("Discover product clicked", {
        supplier: product.Supplier.urlId,
        product: product.name,
        source: "Recently added",
        containsPreOrderProductItem: product.containsPreOrderProductItem,
        sessionId,
        ...getSellarWebsiteUtms(),
      });
    }
  };

  const { isDisplaySizeSatisfied, target } = useDisplaySizeVariant();
  const is2xl = isDisplaySizeSatisfied("xl");
  const is4xl = isDisplaySizeSatisfied("4xl");
  return (
    <>
      {showFilters && (
        <>
          {showAwardBadge ? (
            <AwardSpotlightFilterContainerCard
              label="Search for a winner:"
              desktopClassName="px-4 pt-4"
            />
          ) : (
            <SpotlightFilterContainerCard
              label="Filter by:"
              desktopClassName="px-4 pt-4"
            />
          )}
        </>
      )}
      <div className={twMerge("px-4 pb-4", className)} ref={target}>
        {isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            <div
              className={twMerge(
                "grid grid-cols-1 gap-6",
                is2xl && "grid-cols-2",
                is4xl && "grid-cols-3",
              )}
            >
              {productListQuery.data?.pages.map((page) =>
                page.map((product) => (
                  <SpotlightProductCard
                    key={product.id}
                    product={product}
                    onClick={() => onProductClicked(product)}
                    showAwardBadge={showAwardBadge}
                  />
                )),
              )}
            </div>
          </>
        )}
      </div>
      {selectedProduct && (
        <>
          <DiscoveryProductDialog
            productId={selectedProduct.id}
            supplierId={selectedProduct.Supplier?.id}
            active={!!selectedProduct}
            showAwardBadge={showAwardBadge}
            onDismiss={() => {
              searchParams.delete("productId");
              setSearchParams(searchParams);
              setSelectedProduct(undefined);
            }}
            setBestPricePromiseDialogActive={() => {
              setBestPricePromiseDialogActive(true);
            }}
          />

          <BestPricePromiseDialog
            productName={selectedProduct.name}
            buyerName={selectedProduct.name}
            supplierName={selectedProduct.Supplier?.Business?.name}
            active={bestPricePromiseDialogActive}
            onDismiss={() => setBestPricePromiseDialogActive(false)}
          />
        </>
      )}
    </>
  );
};
